import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isValidating: false,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/login', {
            login: credentials.email,
            password: credentials.password,
          });
          const data = response.data;
          if (data.requires_2fa) {
            set({ isLoading: false });
            return { success: true, requires_2fa: true, user_id: data.user_id, user_type: data.user_type };
          }
          const { user, token } = data;
          if (user.user_type !== 'admin') {
            const msg =
              user.user_type === 'provider'
                ? 'Access denied. Please use the Provider login page.'
                : user.user_type === 'delivery'
                  ? 'Access denied. Please use the Delivery login page.'
                  : 'Access denied. Admins only.';
            set({ error: msg, isLoading: false });
            return { success: false, error: msg };
          }
          set({ user, token, isLoading: false });
          // Store token with creation timestamp for expiry tracking (24h expiry)
          localStorage.setItem('token', token);
          localStorage.setItem('token_created_at', Date.now().toString());
          return { success: true };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Login failed';
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      verify2fa: async (userId, code) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/verify-2fa-login', { user_id: userId, code });
          const data = response.data;
          const { user, token } = data.data || data;
          if (user.user_type !== 'admin') {
            const msg = 'Access denied. Admins only.';
            set({ error: msg, isLoading: false });
            return { success: false, error: msg };
          }
          set({ user, token, isLoading: false });
          localStorage.setItem('token', token);
          localStorage.setItem('token_created_at', Date.now().toString());
          return { success: true };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Verification failed';
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      validateToken: async (retries = 1) => {
        const token = get().token || localStorage.getItem('token');
        if (!token) {
          set({ user: null, token: null });
          return false;
        }

        // Proactive token expiry check — tokens expire after 24h
        const tokenCreatedAt = localStorage.getItem('token_created_at');
        if (tokenCreatedAt) {
          const elapsed = Date.now() - parseInt(tokenCreatedAt, 10);
          const TWENTY_THREE_HOURS = 23 * 60 * 60 * 1000; // Check a bit early
          if (elapsed > TWENTY_THREE_HOURS) {
            set({ user: null, token: null, isValidating: false });
            localStorage.removeItem('token');
            localStorage.removeItem('token_created_at');
            localStorage.removeItem('admin-auth-storage');
            return false;
          }
        }

        set({ isValidating: true });
        try {
          const response = await api.get('/user/profile');
          const userData = response.data?.data ?? response.data?.user ?? null;
          if (userData) {
            set({ user: userData, isValidating: false });
            return true;
          }
          throw new Error('Invalid response');
        } catch (error) {
          if (error.response?.status === 401) {
            set({ user: null, token: null, isValidating: false });
            localStorage.removeItem('token');
            localStorage.removeItem('token_created_at');
            localStorage.removeItem('admin-auth-storage');
            return false;
          }
          if (retries > 0 && !error.response) {
            await new Promise((r) => setTimeout(r, 1000));
            return get().validateToken(retries - 1);
          }
          set({ isValidating: false });
          // If we got a non-401 error (network issue, etc.), keep the token — it might still be valid
          return true;
        }
      },

      logout: (sessionExpired = false) => {
        set({ user: null, token: null, error: null });
        localStorage.removeItem('token');
        localStorage.removeItem('token_created_at');
        localStorage.removeItem('admin-auth-storage');
        if (sessionExpired) {
          window.location.href = '/admin/?session_expired=1';
        } else {
          window.location.href = '/admin/';
        }
      },

      setUser: (user, token) => {
        set({ user, token });
        if (token) localStorage.setItem('token', token);
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          localStorage.setItem('token', state.token);
          // Restore token_created_at if it was persisted (e.g. from session restore)
          if (!localStorage.getItem('token_created_at')) {
            localStorage.setItem('token_created_at', Date.now().toString());
          }
        }
      },
    },
  ),
);

export default useAuthStore;
export { useAuthStore };
